import React, { useMemo, useState } from 'react';
import { BarChart3, CalendarRange, Clock3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AttendanceRecord } from '../../../context/LeadAttendanceContext';

type StatsRange = 'weekly' | 'monthly' | 'custom';

interface GroupStatsTabProps {
  history: AttendanceRecord[];
}

const getToday = () => new Date().toISOString().slice(0, 10);

export default function GroupStatsTab({ history }: GroupStatsTabProps) {
  const { t } = useTranslation();
  const [range, setRange] = useState<StatsRange>('weekly');
  const [customFrom, setCustomFrom] = useState(getToday());
  const [customTo, setCustomTo] = useState(getToday());

  const filteredHistory = useMemo(() => {
    if (history.length === 0) return [];

    if (range === 'custom') {
      const from = new Date(`${customFrom}T00:00:00Z`);
      const to = new Date(`${customTo}T23:59:59Z`);
      return history.filter((entry) => {
        const date = new Date(entry.date);
        return date >= from && date <= to;
      });
    }

    const days = range === 'weekly' ? 7 : 30;
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    return history.filter((entry) => new Date(entry.date) >= cutoff);
  }, [customFrom, customTo, history, range]);

  const summary = useMemo(() => {
    return filteredHistory.reduce(
      (acc, record) => {
        record.attendances.forEach((entry) => {
          acc.total += 1;
          acc[entry.status] += 1;
        });
        return acc;
      },
      { total: 0, PRESENT: 0, ABSENT: 0, LATE: 0 }
    );
  }, [filteredHistory]);

  return (
    <section className="space-y-4">
      <header className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <h3 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
          <BarChart3 className="h-4 w-4" />
          {t('groupStatsTitle')}
        </h3>
        <p className="mt-1 text-sm text-slate-600">{t('statsPlaceholder')}</p>
      </header>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="space-y-1 rounded-xl border border-slate-200 bg-white p-3 text-sm">
          <span className="inline-flex items-center gap-1 font-medium text-slate-700">
            <Clock3 className="h-3.5 w-3.5" />
            {t('rangeLabel')}
          </span>
          <select
            className="h-10 w-full rounded-lg border border-slate-300 px-2 text-sm"
            value={range}
            onChange={(event) => setRange(event.target.value as StatsRange)}
          >
            <option value="weekly">{t('weekly')}</option>
            <option value="monthly">{t('monthly')}</option>
            <option value="custom">{t('custom')}</option>
          </select>
        </label>

        {range === 'custom' && (
          <>
            <label className="space-y-1 rounded-xl border border-slate-200 bg-white p-3 text-sm">
              <span className="inline-flex items-center gap-1 font-medium text-slate-700">
                <CalendarRange className="h-3.5 w-3.5" />
                {t('fromLabel')}
              </span>
              <input
                type="date"
                className="h-10 w-full rounded-lg border border-slate-300 px-2 text-sm"
                value={customFrom}
                onChange={(event) => setCustomFrom(event.target.value)}
              />
            </label>

            <label className="space-y-1 rounded-xl border border-slate-200 bg-white p-3 text-sm">
              <span className="inline-flex items-center gap-1 font-medium text-slate-700">
                <CalendarRange className="h-3.5 w-3.5" />
                {t('toLabel')}
              </span>
              <input
                type="date"
                className="h-10 w-full rounded-lg border border-slate-300 px-2 text-sm"
                value={customTo}
                onChange={(event) => setCustomTo(event.target.value)}
              />
            </label>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { labelKey: 'statsTotal', value: summary.total },
          { labelKey: 'statsPresent', value: summary.PRESENT },
          { labelKey: 'statsAbsent', value: summary.ABSENT },
          { labelKey: 'statsLate', value: summary.LATE },
        ].map((card) => (
          <article key={card.labelKey} className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-xs text-slate-500">{t(card.labelKey)}</p>
            <p className="mt-1 text-xl font-semibold text-slate-900">{card.value}</p>
          </article>
        ))}
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-3">
        <h4 className="text-sm font-semibold text-slate-700">{t('savedReports')} ({filteredHistory.length})</h4>

        {filteredHistory.length === 0 ? (
          <p className="mt-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-500">
            {t('noAttendanceRecords')}
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {filteredHistory.map((report) => (
              <li key={report.createdAt} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="font-medium text-slate-900">{new Date(report.date).toLocaleDateString()}</p>
                <p className="text-xs text-slate-600">{report.generalNotes || t('noGeneralNotes')}</p>
                <p className="mt-1 text-xs text-slate-500">{report.attendances.length} {t('attendanceEntries')}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

